import styled from 'styled-components';
import type { StopsFilterChange } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import { getStopFilterDirectRoutes } from '@/app/components/hotel-detail/changeTransport/ChangeTransportStopFilter';
import { useTransportPageFilterTracking } from '@/app/components/hotel-detail/changeTransport/hooks/useTransportPageFilterTracking';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import {
  EvoFilterLoadingLayer,
  EvoFilterRadio,
  EvoFilterSection,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoFilterShared';
import {
  buildEvoStopsNewState,
  deriveEvoStopsOption,
  getEvoStopsOptions,
  type EvoStopsOption,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/evoStopsFilter.utils';
import { useTranslation } from '@/app/components/translations/Translation';

const StopsRadio = styled(EvoFilterRadio)`
  width: 100%;
`;

const STOPS_LOADING_ORIGINS: TransportFiltersOrigin[] = [
  'STOPS_OUTBOUND_FILTER',
  'STOPS_RETURN_FILTER',
  'DIRECT_FLIGHTS_FILTER',
  'STOPS_CLEAR_ALL_FILTER',
];

type EvoStopsFilterProps = {
  outboundStops?: StopsFilter[];
  inboundStops?: StopsFilter[];
  directOnly?: DirectOnlyFilter;
  onChange: (filterChange: StopsFilterChange) => void;
};

export function EvoStopsFilter(props: EvoStopsFilterProps) {
  const { outboundStops, inboundStops, directOnly, onChange } = props;
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  const stopsLabels: { [key in EvoStopsOption]: string } = {
    ANY: t('vacation.oss.header.widget.flight_class.any'),
    DIRECT: t('vacation.oss.change_transport.direct'),
    UP_TO_ONE: t('vacation.oss.change_transport.filters.stops.up_to_one'),
    UP_TO_TWO: t('vacation.oss.change_transport.filters.stops.up_to_two'),
  };

  const stopsState = { outboundStops, inboundStops, directOnly };
  const options = getEvoStopsOptions(stopsState);
  const currentOption = deriveEvoStopsOption(stopsState);

  if (options.length <= 1) return null;

  const sendBoundTrackingEvents = (value: StopsFilterValue) => {
    const bounds: { type: BoundType; origin: StopsFilterEventOrigin }[] = [
      { type: 'outbound', origin: 'STOPS_OUTBOUND_FILTER' },
      { type: 'inbound', origin: 'STOPS_RETURN_FILTER' },
    ];
    bounds.forEach(({ type, origin }) => {
      const bound = type === 'outbound' ? outboundStops : inboundStops;
      if (!bound || bound.length === 0) return;
      sendStratrackFlightPageFilterEvent({ filterName: origin, value, deselectFlag: false });
      sendCuriosityClickEvent({
        event_name: `click_on_${type}_stops_filters`,
        event_context: 'change_transport_page',
        event_value: value,
      });
    });
  };

  const onOptionSelect = (option: EvoStopsOption) => {
    if (option === currentOption) return;
    const newState = buildEvoStopsNewState(option, stopsState);

    if (option === 'DIRECT' && directOnly) {
      const directChange = getStopFilterDirectRoutes(
        true,
        directOnly,
        sendCuriosityClickEvent,
        sendStratrackFlightPageFilterEvent
      );
      onChange({ newState, event: directChange.event });
      return;
    }
    if (option === 'DIRECT') {
      sendBoundTrackingEvents('NO_STOPS');
      onChange({ newState, event: { type: 'FILTER', origin: 'STOPS_OUTBOUND_FILTER' } });
      return;
    }
    if (option === 'ANY') {
      onChange({ newState, event: { type: 'FILTER', origin: 'STOPS_CLEAR_ALL_FILTER' } });
      return;
    }
    sendBoundTrackingEvents(option === 'UP_TO_ONE' ? 'ONE' : 'TWO_OR_MORE');
    onChange({ newState, event: { type: 'FILTER', origin: 'STOPS_OUTBOUND_FILTER' } });
  };

  return (
    <EvoFilterSection>
      {options.map((option) => (
        <StopsRadio
          key={option}
          name="evo-stops-filter"
          value={option}
          label={stopsLabels[option]}
          checked={currentOption === option}
          disabled={option === 'DIRECT' && directOnly ? !directOnly.enabled : undefined}
          onChange={() => onOptionSelect(option)}
        />
      ))}
      <EvoFilterLoadingLayer origins={STOPS_LOADING_ORIGINS} />
    </EvoFilterSection>
  );
}
